import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Link, Navigate } from "react-router-dom";
import { Heart, Truck, HandHeart, ArrowRight } from "lucide-react";
import { fmtTimeAgo, currency } from "../lib/format";
import { Badge } from "../components/ui/badge";

export default function Dashboard() {
    const { user, loading } = useAuth();
    const [pledges, setPledges] = useState([]);
    useEffect(() => {
        if (user) api.get("/pledges/mine").then((r) => setPledges(r.data));
    }, [user]);

    if (loading) return <div className="p-16 text-center text-[var(--rl-text-2)]">Loading…</div>;
    if (!user) return <Navigate to="/auth" replace />;

    const byType = { rescue: 0, foster: 0, transport: 0, donation: 0 };
    pledges.forEach((p) => byType[p.type] = (byType[p.type] || 0) + 1);
    const totalPledged = pledges.reduce((s, p) => s + (p.amount || 0), 0);

    return (
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10" data-testid="dashboard-page">
            <div className="flex items-start gap-5 mb-8">
                <div className="w-16 h-16 rounded-full bg-[var(--rl-primary)] text-white font-serif text-3xl flex items-center justify-center">{user.name[0]}</div>
                <div className="flex-1">
                    <div className="eyebrow">{user.role}</div>
                    <h1 className="font-serif text-4xl">{user.name}</h1>
                    <div className="text-sm text-[var(--rl-text-2)]">{user.email} · {user.location || "No location set"}</div>
                </div>
                {user.role === "admin" && (
                    <Link
                        to="/owner/status"
                        data-testid="owner-status-link"
                        className="inline-flex items-center gap-2 rounded-full border border-[var(--rl-primary)] text-[var(--rl-primary)] px-4 py-2 text-sm hover:bg-[#fff5f3]"
                    >
                        Owner Status Dashboard <ArrowRight className="w-4 h-4" />
                    </Link>
                )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                <Stat label="Rescue pledges" value={byType.rescue} icon={<Heart className="w-4 h-4" />} />
                <Stat label="Fosters" value={byType.foster} icon={<HandHeart className="w-4 h-4" />} />
                <Stat label="Transports" value={byType.transport} icon={<Truck className="w-4 h-4" />} />
                <Stat label="Donated" value={currency(totalPledged)} icon={<Heart className="w-4 h-4" />} />
            </div>

            <section className="mb-12">
                <h2 className="font-serif text-3xl mb-5">My pledges</h2>
                {pledges.length === 0 ? (
                    <div className="card-warm rounded-xl p-10 text-center">
                        <p className="text-[var(--rl-text-2)]">You haven't pledged yet.</p>
                        <Link to="/at-risk" className="inline-flex items-center gap-1.5 mt-3 text-[var(--rl-primary)] font-semibold">
                            See dogs at risk <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                ) : (
                    <div className="grid gap-3">
                        {pledges.map((p) => (
                            <Link to={`/dogs/${p.dog_id}`} key={p.id} className="card-warm rounded-lg p-4 flex items-center gap-4" data-testid={`my-pledge-${p.id}`}>
                                <Badge className="uppercase tracking-widest text-[10px] bg-[var(--rl-cream)] text-[var(--rl-text)] hover:bg-[var(--rl-sage)]">{p.type}</Badge>
                                <div className="flex-1">
                                    <div className="font-semibold">{p.dog_name}</div>
                                    {p.message && <div className="text-xs text-[var(--rl-text-2)] mt-0.5">"{p.message}"</div>}
                                </div>
                                <div className="text-right">
                                    {p.amount ? <div className="font-serif text-lg">{currency(p.amount)}</div> : null}
                                    <div className="text-xs text-[var(--rl-text-3)]">{fmtTimeAgo(p.created_at)}</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}

function Stat({ label, value, icon }) {
    return (
        <div className="card-warm rounded-xl p-5">
            <div className="flex items-center gap-2 text-[var(--rl-primary)]">{icon}<span className="eyebrow">{label}</span></div>
            <div className="font-serif text-3xl mt-2">{value}</div>
        </div>
    );
}
